import React from "react";
import { FaPlay, FaRocket, FaUsers, FaStar } from "react-icons/fa6";
import { BTN_SIZE, Container, GradientButton, H1_SIZE } from "./ui";

// Hero artwork (laptop + tech icons), shown in the right column.
const heroImg = "/img/hero-bg1.jpg";

// Fades all four edges of the image to transparent (two linear masks intersected),
// so the photo's own background never shows as a box against the section.
const fadeX = "linear-gradient(to right, transparent 0%, #000 14%, #000 88%, transparent 100%)";
const fadeY = "linear-gradient(to bottom, transparent 0%, #000 10%, #000 84%, transparent 100%)";
const edgeFade = {
  WebkitMaskImage: `${fadeX}, ${fadeY}`,
  WebkitMaskComposite: "source-in",
  maskImage: `${fadeX}, ${fadeY}`,
  maskComposite: "intersect",
};

// Phone heading size: "Modern Web Development" fits on one line at 360-430px wide screens.
const PHONE_H1 = "max-sm:text-[clamp(20px,6.3vw,27px)] max-sm:leading-[1.3]";

const stats = [
  { icon: FaRocket, value: "100+", label: "Projects Delivered", tone: "bg-violet-100 text-violet-600" },
  { icon: FaUsers, value: "50+", label: "Happy Clients", tone: "bg-blue-100 text-blue-600" },
  { icon: FaStar, value: "4.9/5", label: "Client Satisfaction", tone: "bg-orange-100 text-orange-500" },
];

const Hero = () => (
  <section
    id="technologies"
    className="relative overflow-hidden bg-gradient-to-r from-[#FAFAFC] via-[#F7F8FE] to-[#F1F4FE] pb-[48px] pt-[110px] xl:flex xl:min-h-[min(40.6vw,780px)] xl:items-center xl:pb-[40px] xl:pt-[calc(clamp(76px,5.83vw,112px)+16px)]"
  >

    <Container className="relative grid max-w-[1900px] items-center gap-[32px] xl:grid-cols-[44%_1fr] xl:gap-[clamp(16px,2vw,48px)] xl:pl-[clamp(32px,4.375vw,84px)] xl:pr-[clamp(24px,3.125vw,60px)]">
      {/* Phones (<640px, `max-sm:`) get the centred mobile design; tablet/desktop are unchanged. */}
      <div className="max-w-[640px] max-sm:text-center xl:max-w-none">
        <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-[12px] py-1 text-[12px] font-semibold uppercase tracking-wide text-ink/80 ring-1 ring-blue-100 max-sm:bg-[#E9F0FF] max-sm:px-[16px] max-sm:py-[6px] max-sm:text-[#1E3A8A] max-sm:ring-0">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          We build digital experiences
        </span>

        <h1 className={`mt-4 font-extrabold tracking-tight text-ink ${H1_SIZE} max-sm:mt-[18px] ${PHONE_H1}`}>
          Modern Web Development
        </h1>
        <h1 className={`font-extrabold tracking-tight ${H1_SIZE} ${PHONE_H1}`}>
          <span className="text-gradient">for Real Growth</span>
        </h1>

        <p className="mt-4 max-w-xl text-[14px] leading-relaxed text-slate-500 max-sm:mx-auto max-sm:mt-[14px] max-sm:text-slate-600 sm:text-[14px] xl:max-w-[700px]">
          We create fast, scalable, and user-friendly web solutions that help businesses grow in the digital world.
          From stunning websites to powerful web applications, we turn ideas into reality.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-[14px] max-sm:mt-[24px] max-sm:justify-center max-sm:gap-[10px]">
          <GradientButton href="/contact-us" className="max-sm:px-[20px] max-sm:text-[14px]">
            Get Started
          </GradientButton>
          <a
            href="/our-completed-projects"
            className={`inline-flex items-center justify-center rounded-full bg-white font-semibold text-ink ring-1 ring-slate-200 transition hover:ring-brand-blue ${BTN_SIZE} whitespace-nowrap !gap-[12px] !pl-[6px] !pr-[24px] max-sm:!gap-[10px] max-sm:!pr-[18px] max-sm:text-[14px] xl:!gap-[clamp(10px,0.8vw,14px)] xl:!pl-[clamp(5px,0.35vw,7px)] xl:!pr-[clamp(20px,1.6vw,30px)] max-sm:shadow-[0_10px_24px_-16px_rgba(40,60,140,0.35)]`}
          >
            {/* play badge sits almost against the left edge and fills most of the button height (as in the design) */}
            <span className="inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#2F6BFF] to-[#8B3DF5] text-[13px] text-white shadow-[0_6px_14px_-6px_rgba(91,61,245,0.7)] xl:h-[clamp(32px,2.35vw,46px)] xl:w-[clamp(32px,2.35vw,46px)] xl:text-[clamp(11px,0.85vw,16px)]">
              <FaPlay className="ml-0.5" />
            </span>
            Watch Our Work
          </a>
        </div>

        <div className="mt-[28px] grid grid-cols-3 sm:mt-[40px]">
          {stats.map((s, i) => (
            <div
              key={s.label}
              className={`flex min-w-0 items-center gap-[10px] pr-[8px] text-left sm:pr-[14px] ${i > 0 ? "[border-left:1px_solid_#E2E8F0] pl-[12px] sm:pl-[16px]" : ""}`}
            >
              <span className={`inline-flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full text-[18px] max-sm:hidden ${s.tone}`}>
                <s.icon />
              </span>
              <div>
                <p className="text-[22px] font-extrabold leading-none text-ink max-sm:text-[18px]">{s.value}</p>
                <p className="mt-1 whitespace-nowrap text-[13px] text-slate-500 max-sm:mt-[6px] max-sm:text-[12px]">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Artwork: right column on desktop, below the text on mobile/tablet */}
      <div className="flex justify-center xl:justify-end">
        <img
          src={heroImg}
          alt="Developer workspace with a laptop and modern web technologies"
          className="h-auto max-h-[480px] w-full max-w-[min(100%,860px)] select-none object-contain xl:w-auto"
          style={edgeFade}
        />
      </div>
    </Container>
  </section>
);

export default Hero;
